/**
 * BKOnScents — Theme JavaScript
 * Shopify-compatible. Quiet luxury, editorial interactions.
 * Refined, not reactive.
 */

'use strict';

// ─── Utility ────────────────────────────────────────────────────────────────

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const raf = (fn) => requestAnimationFrame(fn);

/** Lightweight RAF-based debounce for scroll/resize handlers */
const rafDebounce = (fn) => {
  let scheduled = false;
  return (...args) => {
    if (scheduled) return;
    scheduled = true;
    raf(() => {
      fn(...args);
      scheduled = false;
    });
  };
};

// ─── Navigation Scroll Effect ────────────────────────────────────────────────

const initNavScroll = () => {
  const nav = document.querySelector('.site-header');
  if (!nav) return;

  const toggle = rafDebounce(() => {
    nav.classList.toggle('nav-scrolled', window.scrollY > 50);
  });

  window.addEventListener('scroll', toggle, { passive: true });
  toggle(); // set correct state on load
};

// ─── Mobile Menu ─────────────────────────────────────────────────────────────

const initMobileMenu = () => {
  const hamburger = document.querySelector('.hamburger-btn');
  const mobileMenu = document.querySelector('.mobile-menu-overlay');
  const mobileMenuClose = document.querySelector('.mobile-menu-close');
  if (!hamburger) return;

  const navLinks = mobileMenu ? mobileMenu.querySelectorAll('a') : [];
  const focusableSelectors = 'a, button, [tabindex]:not([tabindex="-1"])';
  let focusables = [];

  const open = () => {
    document.body.classList.add('menu-open');
    hamburger.setAttribute('aria-expanded', 'true');
    hamburger.classList.add('active');
    if (mobileMenu) mobileMenu.classList.add('active');
    focusables = mobileMenu
      ? [...mobileMenu.querySelectorAll(focusableSelectors)]
      : [];
    focusables[0]?.focus();
  };

  const close = () => {
    document.body.classList.remove('menu-open');
    hamburger.setAttribute('aria-expanded', 'false');
    hamburger.classList.remove('active');
    if (mobileMenu) mobileMenu.classList.remove('active');
    hamburger.focus();
  };

  const isOpen = () => document.body.classList.contains('menu-open');

  // Focus trap
  const trapFocus = (e) => {
    if (!isOpen() || e.key !== 'Tab') return;
    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  hamburger.setAttribute('aria-expanded', 'false');
  hamburger.addEventListener('click', () => (isOpen() ? close() : open()));

  if (mobileMenuClose) {
    mobileMenuClose.addEventListener('click', close);
  }

  navLinks.forEach((link) => link.addEventListener('click', close));

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen()) close();
    trapFocus(e);
  });

  // Overlay backdrop click
  document.addEventListener('click', (e) => {
    if (
      isOpen() &&
      mobileMenu &&
      !mobileMenu.contains(e.target) &&
      e.target !== hamburger
    ) {
      close();
    }
  });
};

// ─── Scroll Reveal ───────────────────────────────────────────────────────────

const initScrollReveal = () => {
  const reveals = document.querySelectorAll('.reveal');
  const staggerParents = document.querySelectorAll('.reveal-stagger');

  if (!reveals.length && !staggerParents.length) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('active');
        observer.unobserve(entry.target);
      });
    },
    { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
  );

  reveals.forEach((el) => observer.observe(el));

  // Stagger: activate parent; CSS nth-child delays handle children
  const staggerObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('active');
        staggerObserver.unobserve(entry.target);
      });
    },
    { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
  );

  staggerParents.forEach((el) => staggerObserver.observe(el));
};

// ─── Smooth Scroll ───────────────────────────────────────────────────────────

const initSmoothScroll = () => {
  const NAV_OFFSET = 80;

  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href^="#"]');
    if (!link) return;

    const id = link.getAttribute('href');
    if (id === '#') return;

    const target = document.querySelector(id);
    if (!target) return;

    e.preventDefault();

    const top =
      target.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;

    window.scrollTo({
      top,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  });
};

// ─── Marquee Pause on Hover ──────────────────────────────────────────────────

const initMarquee = () => {
  const marquees = document.querySelectorAll('.marquee-strip, .marquee, [data-marquee]');
  if (!marquees.length) return;

  marquees.forEach((el) => {
    el.addEventListener('mouseenter', () => el.classList.add('marquee-paused'));
    el.addEventListener('mouseleave', () =>
      el.classList.remove('marquee-paused')
    );
  });
};

// ─── Product Card Hover ──────────────────────────────────────────────────────

const initProductCards = () => {
  const images = document.querySelectorAll(
    '.product-card .product-image, .product-card img'
  );
  if (!images.length) return;

  images.forEach((img) => {
    img.addEventListener('mouseenter', () => img.classList.add('image-hovered'));
    img.addEventListener('mouseleave', () =>
      img.classList.remove('image-hovered')
    );
  });
};

// ─── Newsletter Form ─────────────────────────────────────────────────────────
//
// For Shopify: forms with action="/contact#ContactFooter" or similar are
// submitted natively by Shopify. We intercept only forms marked
// [data-newsletter] or .newsletter-form that do NOT have a Shopify form
// action (i.e. pure JS validation + fetch). If Shopify handles submission
// natively, this just adds client-side validation before submit.

const initNewsletter = () => {
  const forms = document.querySelectorAll(
    '.newsletter-form, [data-newsletter]'
  );
  if (!forms.length) return;

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  forms.forEach((form) => {
    const isShopifyForm =
      form.getAttribute('action')?.includes('/contact') ||
      form.getAttribute('method') === 'post';

    if (isShopifyForm) {
      // Just validate — let Shopify handle submission
      form.addEventListener('submit', (e) => {
        const input = form.querySelector('input[type="email"]');
        const email = input?.value.trim() ?? '';
        if (!EMAIL_RE.test(email)) {
          e.preventDefault();
          input?.focus();
          input?.classList.add('input-error');
          input?.addEventListener(
            'input',
            () => input.classList.remove('input-error'),
            { once: true }
          );
        }
      });
    } else {
      // Pure JS form — intercept and provide visual feedback
      form.addEventListener('submit', (e) => {
        e.preventDefault();

        const input = form.querySelector('input[type="email"]');
        const email = input?.value.trim() ?? '';

        if (!EMAIL_RE.test(email)) {
          input?.focus();
          input?.classList.add('input-error');
          input?.addEventListener(
            'input',
            () => input.classList.remove('input-error'),
            { once: true }
          );
          return;
        }

        input?.classList.remove('input-error');

        const success = document.createElement('p');
        success.className = 'newsletter-success';
        success.textContent = 'Thank you for subscribing.';
        success.style.opacity = '0';
        success.style.transition = 'opacity 0.6s ease';

        form.style.transition = 'opacity 0.4s ease';
        form.style.opacity = '0';

        setTimeout(() => {
          form.replaceWith(success);
          raf(() => {
            success.style.opacity = '1';
          });
        }, 420);
      });
    }
  });
};

// ─── Hero Parallax ───────────────────────────────────────────────────────────

const initHeroParallax = () => {
  if (prefersReducedMotion()) return;

  const hero = document.querySelector('.hero, [data-hero]');
  const heroContent = hero?.querySelector('.hero-text, [data-hero-content]');
  const heroImage = hero?.querySelector('.hero-image, [data-hero-image]');

  if (!hero || (!heroContent && !heroImage)) return;

  const MAX = 100;

  const update = rafDebounce(() => {
    const rect = hero.getBoundingClientRect();
    const inView = rect.bottom > 0 && rect.top < window.innerHeight;
    if (!inView) return;

    const scrolled = Math.min(window.scrollY, MAX / 0.15);

    if (heroContent) {
      const y = Math.min(scrolled * 0.15, MAX);
      heroContent.style.transform = `translateY(-${y}px)`;
    }

    if (heroImage) {
      const y = Math.min(scrolled * 0.05, MAX);
      heroImage.style.transform = `translateY(${y}px)`;
    }
  });

  window.addEventListener('scroll', update, { passive: true });
};

// ─── Announcement Bar Dismiss ─────────────────────────────────────────────────

const initAnnouncementBar = () => {
  const bar = document.querySelector(
    '.announcement-bar, [data-announcement]'
  );
  const closeBtn = bar?.querySelector(
    '.announcement-close, [data-dismiss]'
  );

  if (!bar || !closeBtn) return;

  closeBtn.addEventListener('click', () => {
    const height = bar.offsetHeight;

    bar.style.overflow = 'hidden';
    bar.style.transition = 'height 0.4s ease, opacity 0.4s ease';
    bar.style.height = `${height}px`;

    raf(() => {
      bar.style.height = '0';
      bar.style.opacity = '0';
    });

    bar.addEventListener(
      'transitionend',
      () => {
        bar.style.display = 'none';
        // Reclaim body padding that was reserved for the bar
        const current =
          parseInt(getComputedStyle(document.body).paddingTop, 10) || 0;
        document.body.style.paddingTop = `${Math.max(
          0,
          current - height
        )}px`;
      },
      { once: true }
    );
  });
};

// ─── Lazy Image Fade-in ───────────────────────────────────────────────────────

const initLazyImages = () => {
  const images = document.querySelectorAll(
    'img[loading="lazy"], img[data-lazy]'
  );
  if (!images.length) return;

  const applyFade = (img) => {
    if (img.dataset.fadeInit) return;
    img.dataset.fadeInit = '1';
    img.style.opacity = img.complete ? '1' : '0';
    img.style.transition = 'opacity 0.7s ease';

    if (!img.complete) {
      img.addEventListener(
        'load',
        () => {
          img.style.opacity = '1';
        },
        { once: true }
      );
    }
  };

  const imgObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          applyFade(entry.target);
          imgObserver.unobserve(entry.target);
        }
      });
    },
    { rootMargin: '200px' }
  );

  images.forEach((img) => {
    if (img.getBoundingClientRect().top < window.innerHeight) {
      applyFade(img); // already visible on load
    } else {
      imgObserver.observe(img);
    }
  });
};

// ─── Scroll Progress Bar ─────────────────────────────────────────────────────

const initScrollProgress = () => {
  if (prefersReducedMotion()) return;

  const bar = document.createElement('div');
  bar.setAttribute('aria-hidden', 'true');
  bar.style.cssText = `
    position: fixed;
    top: 0; left: 0;
    width: 100%; height: 2px;
    background: var(--color-accent, #8b7355);
    transform-origin: left;
    transform: scaleX(0);
    z-index: 9999;
    pointer-events: none;
    will-change: transform;
    transition: transform 0.05s linear;
  `;
  document.documentElement.prepend(bar);

  const update = rafDebounce(() => {
    const { scrollTop, scrollHeight, clientHeight } =
      document.documentElement;
    const progress = scrollTop / (scrollHeight - clientHeight) || 0;
    bar.style.transform = `scaleX(${progress})`;
  });

  window.addEventListener('scroll', update, { passive: true });
};

// ─── Collection Card Interaction ─────────────────────────────────────────────

const initCollectionCards = () => {
  const cards = document.querySelectorAll(
    '.collection-card, [data-collection-card]'
  );
  if (!cards.length) return;

  const activate = (card) => card.classList.add('card-active');
  const deactivate = (card) => card.classList.remove('card-active');

  cards.forEach((card) => {
    card.addEventListener('mouseenter', () => activate(card));
    card.addEventListener('mouseleave', () => deactivate(card));
    card.addEventListener('touchstart', () => activate(card), {
      passive: true,
    });
    card.addEventListener(
      'touchend',
      () => {
        setTimeout(() => deactivate(card), 400);
      },
      { passive: true }
    );
  });
};

// ─── Cart AJAX ───────────────────────────────────────────────────────────────
//
// Intercepts "Add to cart" buttons on product cards and the product page.
// Posts to Shopify's /cart/add.js endpoint, then updates the cart badge.
// Buttons should have:
//   data-variant-id="{{ variant.id }}"
//   data-product-title="{{ product.title }}"
// and optionally a quantity field nearby.

const initCartAjax = () => {
  // Update every cart count badge in the nav
  const updateCartBadge = (count) => {
    document
      .querySelectorAll('.cart-count, [data-cart-count]')
      .forEach((el) => {
        el.textContent = count > 0 ? count : '';
        if (count > 0) {
          el.removeAttribute('hidden');
          el.style.display = '';
        } else {
          el.style.display = 'none';
        }
      });
  };

  // Fetch current cart count on page load
  const refreshCartCount = () => {
    fetch('/cart.js')
      .then((r) => r.json())
      .then((data) => updateCartBadge(data.item_count))
      .catch(() => {});
  };

  // Add to cart via AJAX
  const addToCart = (variantId, quantity, button) => {
    if (!variantId) return;

    button.disabled = true;
    button.classList.add('adding');
    const originalText = button.textContent;
    button.textContent = 'Adding\u2026';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity }),
    })
      .then((res) => {
        if (!res.ok) throw new Error('Add to cart failed');
        return res.json();
      })
      .then(() => {
        button.classList.remove('adding');
        button.classList.add('added');
        button.textContent = 'Added!';

        // Refresh badge
        refreshCartCount();

        // Reset button after 2 s
        setTimeout(() => {
          button.classList.remove('added');
          button.textContent = originalText;
          button.disabled = false;
        }, 2000);
      })
      .catch(() => {
        button.classList.remove('adding');
        button.textContent = originalText;
        button.disabled = false;
      });
  };

  // Delegate clicks on [data-add-to-cart] or .add-to-bag-btn[data-variant-id]
  document.addEventListener('click', (e) => {
    const btn = e.target.closest(
      '[data-add-to-cart], .add-to-bag-btn[data-variant-id]'
    );
    if (!btn) return;

    e.preventDefault();

    const variantId = btn.dataset.variantId || btn.dataset.addToCart;
    const qty = parseInt(
      btn.closest('[data-product-form]')?.querySelector('[name="quantity"]')
        ?.value ?? '1',
      10
    );

    addToCart(variantId, qty || 1, btn);
  });

  // Also intercept Shopify product forms that have [data-product-form]
  document.querySelectorAll('[data-product-form]').forEach((form) => {
    form.addEventListener('submit', (e) => {
      // Only intercept if the form has no action that would normally navigate
      // (i.e. the theme uses AJAX cart)
      const variantSelect = form.querySelector('[name="id"]');
      const variantId = variantSelect?.value;
      const qty =
        parseInt(form.querySelector('[name="quantity"]')?.value ?? '1', 10) ||
        1;
      const submitBtn = form.querySelector('[type="submit"]');

      if (!variantId || !submitBtn) return;

      e.preventDefault();
      addToCart(variantId, qty, submitBtn);
    });
  });

  refreshCartCount();
};

// ─── Init ─────────────────────────────────────────────────────────────────────

const init = () => {
  initNavScroll();
  initMobileMenu();
  initScrollReveal();
  initSmoothScroll();
  initMarquee();
  initProductCards();
  initNewsletter();
  initHeroParallax();
  initAnnouncementBar();
  initLazyImages();
  initScrollProgress();
  initCollectionCards();
  initCartAjax();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
